import IconChevronDown from '@apollo/icons/default/IconChevronDown.svg';
import {
  Button,
  Menu,
  MenuButton,
  MenuContent,
  MenuDivider,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
} from '@apollo/orbit';
import _ from 'lodash';
import React, { useCallback } from 'react';

import { useTrackCustomEvent } from 'src/hooks/useTrackCustomEvent';
import { ErrorInsightsListGroupByColumn } from 'src/lib/graphqlTypes/types';

import { BreakdownOption, GroupOption } from './sharedTypes';

interface Props {
  selectedGroupOption: GroupOption;
  selectedBreakdownOption: BreakdownOption;
  onGroupSelectionChange: ({
    selectedGroupOption,
    selectedBreakdownOption,
  }: {
    selectedGroupOption: GroupOption;
    selectedBreakdownOption: BreakdownOption;
  }) => void;
}

export const GroupByButton = ({
  selectedGroupOption,
  selectedBreakdownOption,
  onGroupSelectionChange,
}: Props) => {
  const trackCustomEvent = useTrackCustomEvent();

  const handleGroupChange = useCallback(
    (value: GroupOption) => {
      trackCustomEvent({
        category: 'Operation Insights Errors',
        action: 'group_by_dropdown_group_by_radio_click',
        label: value,
      });

      const breakdownOverride =
        value === ErrorInsightsListGroupByColumn.SERVICE &&
        selectedBreakdownOption === ErrorInsightsListGroupByColumn.SERVICE
          ? ErrorInsightsListGroupByColumn.CODE
          : value === ErrorInsightsListGroupByColumn.CODE &&
              selectedBreakdownOption === ErrorInsightsListGroupByColumn.CODE
            ? ErrorInsightsListGroupByColumn.SERVICE
            : null;
      onGroupSelectionChange({
        selectedGroupOption: value,
        selectedBreakdownOption: breakdownOverride ?? selectedBreakdownOption,
      });
    },
    [onGroupSelectionChange, selectedBreakdownOption, trackCustomEvent],
  );

  const handleBreakdownChange = useCallback(
    (value: BreakdownOption) => {
      trackCustomEvent({
        category: 'Operation Insights Errors',
        action: 'group_by_dropdown_breakdown_by_radio_click',
        label: value,
      });

      onGroupSelectionChange({
        selectedGroupOption,
        selectedBreakdownOption: value,
      });
    },
    [onGroupSelectionChange, selectedGroupOption, trackCustomEvent],
  );

  const optionDisplayName =
    selectedGroupOption === ErrorInsightsListGroupByColumn.CODE
      ? 'error code'
      : selectedGroupOption;

  const handleMenuOpen = () => {
    trackCustomEvent({
      category: 'Operation Insights Errors',
      action: 'group_by_dropdown_open',
    });
  };
  const handleMenuClose = () => {
    trackCustomEvent({
      category: 'Operation Insights Errors',
      action: 'group_by_dropdown_close',
    });
  };

  return (
    <Menu onOpen={handleMenuOpen} onClose={handleMenuClose}>
      <MenuButton
        as={Button}
        size="md"
        variant="secondary"
        rightIcon={<IconChevronDown />}
      >
        Group by: {_.capitalize(optionDisplayName)}
      </MenuButton>
      <MenuContent>
        <MenuList>
          <MenuOptionGroup
            label="Group by"
            type="radio"
            value={selectedGroupOption}
            onChange={handleGroupChange}
          >
            <MenuItemOption
              value={ErrorInsightsListGroupByColumn.SERVICE}
              type="radio"
            >
              Service
            </MenuItemOption>
            <MenuItemOption
              value={ErrorInsightsListGroupByColumn.CODE}
              type="radio"
            >
              Error code
            </MenuItemOption>
          </MenuOptionGroup>
          <MenuDivider />
          <MenuOptionGroup
            label="Breakdown by"
            type="radio"
            value={selectedBreakdownOption}
            onChange={handleBreakdownChange}
          >
            <MenuItemOption
              value={ErrorInsightsListGroupByColumn.SERVICE}
              type="radio"
              isDisabled={
                selectedGroupOption === ErrorInsightsListGroupByColumn.SERVICE
              }
            >
              Service
            </MenuItemOption>
            <MenuItemOption
              value={ErrorInsightsListGroupByColumn.CODE}
              type="radio"
              isDisabled={
                selectedGroupOption === ErrorInsightsListGroupByColumn.CODE
              }
            >
              Error code
            </MenuItemOption>
            <MenuItemOption
              value={ErrorInsightsListGroupByColumn.PATH}
              type="radio"
            >
              Path
            </MenuItemOption>
          </MenuOptionGroup>
        </MenuList>
      </MenuContent>
    </Menu>
  );
};
